import React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet-async';
import { Zap, Clock, ArrowRight, Timer } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { ErrorBoundary } from './ErrorBoundary';
import { formatDistanceToNow } from 'date-fns';

const ITEMS_PER_PAGE = 12;

async function fetchDailyDeals({ pageParam = 1 }) {
  // Calculate pagination
  const from = (pageParam - 1) * ITEMS_PER_PAGE;
  const to = from + ITEMS_PER_PAGE - 1;
  
  const { data, count, error } = await supabase
    .from('products')
    .select('*', { count: 'exact' })
    .filter('product_tags', 'cs', JSON.stringify(['hot_deal']))
    .gt('discount', 0)
    .order('discount', { ascending: false })
    .order('updated_at', { ascending: false })
    .range(from, to);

  if (error) throw new Error(error.message);
  
  return {
    deals: data || [],
    nextPage: (from + ITEMS_PER_PAGE) < count ? pageParam + 1 : undefined,
    total: count
  };
}

export function DailyDealsPage() {
  const { ref, inView } = useInView();
  
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error
  } = useInfiniteQuery({
    queryKey: ['dailyDealsPaginated'],
    queryFn: fetchDailyDeals,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    refetchInterval: 300000, // Refetch every 5 minutes
  });

  React.useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const title = `Daily Deals - Limited Time Offers ${new Date().getFullYear()}`;
  const description = 'Today\'s best deals on computer hardware, electronics, and tech accessories. Limited time offers with significant savings - grab them before they expire!';
  const keywords = ['daily deals', 'today deals', 'limited time offers', 'tech deals', 'computer hardware sale', 'electronics discount'];

  const allDailyDeals = data?.pages.flatMap(page => page.deals) || [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-gray-200 rounded w-1/3"></div>
            <div className="h-6 bg-gray-200 rounded w-2/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="h-48 bg-gray-100 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg max-w-md">
          <p className="text-red-800">Failed to load daily deals: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords.join(', ')} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="/daily-deals" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <header className="bg-gradient-to-r from-red-600 to-orange-600 text-white">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Zap className="w-8 h-8 mr-3" />
                <h1 className="text-2xl font-semibold text-white">Daily Deals</h1>
              </div>
              <p className="text-xl opacity-90 max-w-3xl mx-auto">
                Limited time offers on the best tech products. These deals expire at midnight - don't miss out!
              </p>
              <div className="mt-4 inline-flex items-center text-sm opacity-75">
                <Clock className="w-4 h-4 mr-2" />
                Updated everyday with new deals
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-8">
          {allDailyDeals.length === 0 && !isLoading ? (
            <div className="text-center py-12">
              <Timer className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Daily Deals Available</h2>
              <p className="text-gray-500 mb-6">Check back later for new daily deals, or explore our price drops.</p>
              <Link 
                to="/price-drops-today" 
                className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                View Price Drops <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          ) : (
            <>
              {allDailyDeals.length > 0 && (
                <div className="mb-8">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Zap className="w-5 h-5 text-red-600 mr-2" />
                        <span className="font-medium text-red-800">
                          {data?.pages[0]?.total || allDailyDeals.length} Daily Deals Found
                        </span>
                      </div>
                      <div className="text-sm text-red-600">
                        ⏰ Limited time offers
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {allDailyDeals.map((deal) => (
                  <ErrorBoundary key={deal.id}>
                    <DailyDealCard deal={deal} />
                  </ErrorBoundary>
                ))}
                
                {/* Loading indicator */}
                {isFetchingNextPage && (
                  <div className="col-span-full py-8 text-center">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]" />
                  </div>
                )}
                
                {/* Infinite scroll trigger */}
                <div ref={ref} className="h-10 col-span-full" />
                
                {/* Load more button (fallback) */}
                {hasNextPage && !isFetchingNextPage && (
                  <div className="col-span-full flex justify-center py-8">
                    <button
                      onClick={() => fetchNextPage()}
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      Load More Daily Deals
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </>
  );
}

function DailyDealCard({ deal }) {
  if (!deal) return null;

  const dealPrice = typeof deal.price === 'string' ? parseFloat(deal.price) : deal.price;
  const originalPrice = typeof deal.price_old === 'string' ? parseFloat(deal.price_old) : deal.price_old;

  if (isNaN(dealPrice) || !originalPrice || isNaN(originalPrice)) {
    return null;
  }

  const discountPercentage = deal.discount 
    ? Math.round(Number(deal.discount))
    : Math.round(((originalPrice - dealPrice) / originalPrice) * 100);

  const partnerTag = import.meta.env.VITE_AMAZON_PARTNER_TAG;
  const amazonUrl = `https://www.amazon.com/dp/${deal.asin}?tag=${partnerTag}`;

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <Link to={`/dp/${deal.asin}`} className="block">
        <div className="relative">
          <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
            {deal.image_url ? (
              <img 
                src={deal.image_url} 
                alt={deal.title}
                className="w-auto h-auto max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            {!deal.image_url && (
              <span className="text-gray-400 text-sm flex items-center justify-center w-full h-full">Product: {deal.asin}</span>
            )}
            {deal.image_url && (
              <span className="text-gray-400 text-sm hidden items-center justify-center w-full h-full">Product: {deal.asin}</span>
            )}
          </div>
          <div className="absolute top-3 right-3">
            <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-sm">
              {discountPercentage}% OFF
            </span>
          </div>
          <div className="absolute top-3 left-3">
            <span className="bg-white text-red-600 px-2 py-1 rounded-full text-xs font-medium shadow-sm">
              Hot Deal
            </span>
          </div>
        </div>
      </Link>

      <div className="p-4">
        <Link to={`/dp/${deal.asin}`} className="block">
          <h3 className="font-medium text-gray-900 mb-2 hover:text-blue-600 line-clamp-2">
            {deal.title}
          </h3>
        </Link>

        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-xl font-bold text-gray-900">
            ${dealPrice.toFixed(2)}
          </span>
          <span className="text-sm text-gray-500 line-through">
            ${originalPrice.toFixed(2)}
          </span>
        </div>

        {deal.updated_at && (
          <div className="bg-red-50 text-red-700 text-sm p-2 rounded mb-4 flex items-center">
            <Timer className="w-4 h-4 mr-1" />
            Updated {formatDistanceToNow(new Date(deal.updated_at), { addSuffix: true })}
          </div>
        )}

        <a
          href={amazonUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium text-center transition-colors"
        >
          Claim Deal Now
        </a>
      </div>
    </div>
  );
}
